/**
 * Vault — Auditoría (F3c). Mockup vault-06.
 *  - admin: log global por rango (7/30/90 días) con tiles, búsqueda, filtro por
 *    acción y export CSV. Solo lectura: nadie borra entradas desde Internal.
 *  - resto: mi actividad (lo que yo hice), paginada.
 * Copy corregido respecto del mockup: el log se conserva de forma permanente y
 * se replica a S3 con Object Lock (CloudTrail registra las llamadas a KMS).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Download, Eye, Pencil, Search, ShieldCheck, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, LoaderBlock, StatCard, fmtNum } from '@/components/internal/shared';
import { ActivityList, useVaultLabels } from '@/components/vault/vault-ui';
import { useVaultAccess } from '@/hooks/useVault';
import { downloadCsv } from '@/lib/csv';
import { VAULT_ALERT_ACTIONS, VAULT_AUDIT_ACTIONS, type VaultAuditEvent } from '../../../../shared/types/vault';

const ALL = '__all__';

const fmtTs = (iso: string, lang: string) => {
  try {
    return new Date(iso).toLocaleString(lang, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
};

function actorLabel(actor: string, t: (k: string, d: string) => string): { name: string; sub: string } {
  if (actor.startsWith('system:')) return { name: t('vault.audit.actor_system', 'Sistema'), sub: actor.slice(7) };
  if (actor.startsWith('link:')) return { name: t('vault.audit.actor_link', 'Link temporal'), sub: actor.slice(5, 13) + '…' };
  return { name: actor.split('@')[0], sub: actor };
}

export function AuditPage() {
  const access = useVaultAccess();
  if (access.isLoading) return <LoaderBlock />;
  return access.data?.isAdmin ? <GlobalAudit /> : <MyActivity />;
}

function MyActivity() {
  const { t } = useTranslation();
  const [cursor, setCursor] = useState<string | null>(null);
  const [pages, setPages] = useState<VaultAuditEvent[]>([]);
  const q = trpc.vault.getMyActivity.useQuery({ cursor: cursor ?? undefined }, { staleTime: 30_000 });
  const items = useMemo(() => [...pages, ...(q.data?.items ?? [])], [pages, q.data]);
  return (
    <div className="mx-auto max-w-4xl space-y-4 p-2 sm:p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <h1 className="font-display text-2xl">{t('vault.audit.my_title', 'Mi actividad en el Vault')}</h1>
          <p className="text-sm text-muted-foreground">{t('vault.audit.my_sub', 'Todo lo que hiciste queda registrado: qué credencial, cuándo y desde dónde. El log es de solo lectura.')}</p>
        </div>
      </div>
      <Card className="p-4" data-tour="my-activity">
        {q.isLoading && !pages.length ? <LoaderBlock /> : <ActivityList items={items} emptyLabel={t('vault.audit.my_empty', 'Todavía no hiciste nada en el Vault.')} />}
        {q.data?.cursor && (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => { setPages(items); setCursor(q.data!.cursor); }} disabled={q.isFetching}>
            {t('vault.audit.more', 'Ver más')}
          </Button>
        )}
      </Card>
    </div>
  );
}

function GlobalAudit() {
  const { t, i18n } = useTranslation();
  const L = useVaultLabels();
  const [days, setDays] = useState('7');
  const [q, setQ] = useState('');
  const [action, setAction] = useState<string>(ALL);
  const log = trpc.vaultAdmin.getAuditLog.useQuery({ days: Number(days) }, { staleTime: 30_000 });

  const items = useMemo(() => log.data?.items ?? [], [log.data]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((e) => {
      if (action !== ALL && e.action !== action) return false;
      if (!needle) return true;
      return [e.actorEmail, e.secretName, e.targetEmail, e.detail, L.action(e.action)].some((v) => (v ?? '').toLowerCase().includes(needle));
    });
  }, [items, q, action, L]);
  const stats = log.data?.stats;

  const exportCsv = () =>
    downloadCsv(
      `vault-auditoria-${log.data?.from}_${log.data?.to}.csv`,
      ['fecha', 'persona', 'acción', 'credencial', 'destinatario', 'ip', 'detalle'],
      filtered.map((e) => [e.ts, e.actorEmail, L.action(e.action), e.secretName ?? '', e.targetEmail ?? '', e.ip ?? '', e.detail ?? '']),
    );

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-2 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display text-2xl">{t('vault.audit.title', 'Auditoría del Vault')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('vault.audit.subtitle', 'Todo acceso a una credencial queda registrado: quién, qué, cuándo y desde dónde. Se conserva de forma permanente, con réplica inmutable en S3.')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('vault.audit.d7', 'Últimos 7 días')}</SelectItem>
              <SelectItem value="30">{t('vault.audit.d30', 'Últimos 30 días')}</SelectItem>
              <SelectItem value="90">{t('vault.audit.d90', 'Últimos 90 días')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} disabled={filtered.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            {t('vault.audit.export', 'Exportar CSV')}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Eye} label={t('vault.audit.tile_reveals', 'Aperturas de credencial')} value={fmtNum(stats?.reveals ?? 0)} tone="blue" />
        <StatCard icon={Users} label={t('vault.audit.tile_actors', 'Personas activas')} value={fmtNum(stats?.actors ?? 0)} tone="purple" />
        <StatCard icon={Pencil} label={t('vault.audit.tile_perms', 'Cambios de permisos')} value={fmtNum(stats?.permissionChanges ?? 0)} tone="green" />
        <StatCard icon={AlertTriangle} label={t('vault.audit.tile_alerts', 'Alertas')} value={fmtNum(stats?.alerts ?? 0)} tone={(stats?.alerts ?? 0) > 0 ? 'rose' : 'green'} />
      </div>

      <Card className="space-y-3 p-4">
        {log.data?.truncated && (
          <p className="rounded-md border border-amber-300/60 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
            {t('vault.audit.truncated_server', 'El rango tiene más de {{n}} eventos: se muestran los más recientes. Achicá el rango para ver el resto.', { n: fmtNum(items.length) })}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('vault.audit.search', 'Buscar por persona, credencial o acción…')} className="pl-8" />
          </div>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('vault.audit.f_action_all', 'Acción: todas')}</SelectItem>
              {VAULT_AUDIT_ACTIONS.map((a) => <SelectItem key={a} value={a}>{L.action(a)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {log.isLoading ? (
          <LoaderBlock />
        ) : filtered.length === 0 ? (
          <EmptyState title={items.length === 0 ? t('vault.audit.empty', 'Sin actividad en este rango') : t('vault.audit.empty_filtered', 'Nada coincide con el filtro')} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('vault.audit.col_date', 'Fecha')}</TableHead>
                  <TableHead>{t('vault.audit.col_actor', 'Persona')}</TableHead>
                  <TableHead>{t('vault.audit.col_action', 'Acción')}</TableHead>
                  <TableHead>{t('vault.audit.col_secret', 'Credencial')}</TableHead>
                  <TableHead className="text-right">{t('vault.audit.col_origin', 'Origen')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.slice(0, 500).map((e) => {
                  const actor = actorLabel(e.actorEmail, t);
                  const alert = (VAULT_ALERT_ACTIONS as readonly string[]).includes(e.action);
                  return (
                    <TableRow key={e.auditId} className={alert ? 'bg-rose-50/60 dark:bg-rose-950/20' : undefined}>
                      <TableCell className="whitespace-nowrap font-mono text-xs">{fmtTs(e.ts, i18n.language)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{actor.name}</div>
                        <div className="text-xs text-muted-foreground">{actor.sub}</div>
                      </TableCell>
                      <TableCell><Badge variant={alert ? 'destructive' : 'secondary'}>{L.action(e.action)}</Badge></TableCell>
                      <TableCell className="max-w-[360px] truncate text-sm" title={e.detail}>
                        {e.secretId && !e.secretId.startsWith('onboarding#') ? <Link to={`/vault/secrets/${e.secretId}`} className="hover:underline">{e.secretName ?? e.secretId}</Link> : e.secretId ? <span>{e.secretName ?? e.secretId}</span> : e.targetEmail ?? e.detail ?? '—'}
                        {e.secretId && e.detail && <span className="text-muted-foreground"> · {e.detail}</span>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right font-mono text-xs text-muted-foreground">{e.ip ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {filtered.length > 500 && <p className="mt-2 text-xs text-muted-foreground">{t('vault.audit.truncated', 'Se muestran las primeras 500; exportá el CSV para ver todo.')}</p>}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {t('vault.audit.footer', 'El log es de solo lectura y se replica a S3 con Object Lock: ni un admin de Internal puede borrar entradas. CloudTrail registra además cada llamada a KMS.')}
        </p>
      </Card>
    </div>
  );
}
